<template>
  <div class="product-recommendations critical-component-hide">
    <content-carousel
      :cards="localProducts"
      :card-type="location === 'cart-drawer' ? 'product-cart-drawer': 'product'"
      carousel-pagination
      :title="liquid.title"
      :product-upsell-splide-settings-cart-drawer="
        location === 'cart-drawer' ? productUpsellSplideSettingsCartDrawer : false
      "
    />
  </div>
</template>

<script>

/**
 * Vue: Product recommendations (product-recommendations)
 * -----------------------------------------------------------------------------
 * Product recommendations section powered by Shopify recommendations API,
 * product list from dynamic source and section settings.
 * - Queries recommendations API for each product in cart and pulls a
 *   recommended product for each product in cart until product count is
 *   reached.
 *
 * @param {Object} [containerRef] - Component container reference.
 * @param {Boolean} [displayEmptyCart] - Display component on cart page when
 * cart is empty.
 * @param {Number|Array} liquid.productId - Product ID.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Section title.
 * @param {Number} [overrideProductId] - Overriding product ID, used instead of
 * current product or cart IDs when provided.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Number} [productCount] - Number of products to display.
 * @param {Object} [productUpsellSplideSettingsCartDrawer] - Enable product
 * upsell splide settings for cart drawer.
 * @param {Object} products - Object to define fallback products.
 * @param {Number} [queryLimit] - Limit the number of cart queries.
 * @param {Object} recommendations - Recommendation settings.
 * @param {String} location - Define location.
 *
 */
import { mapState } from 'vuex'

import ContentCarousel from '~async/content-carousel/content-carousel'

import productQuery from '~graphql/product.gql'
import productRecommendationsQuery from '~graphql/product-recommendations.gql'

import { formatProduct } from '~/format/format-product'
import { encodeLiquidId } from '~/helpers/graphql'

export default {
  name: 'ProductRecommendations',

  components: {
    ContentCarousel,
  },

  props: {
    containerRef: {
      type: Object,
      default: () => ({}),
    },
    displayEmptyCart: {
      type: Boolean,
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        productId: 0,
        sectionId: '',
        title: '',
      }),
      required: true,
    },
    location: {
      type: String,
      default: '',
    },
    overrideProductId: {
      type: Number,
      default: 0,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    productCount: {
      type: Number,
      default: 4,
      required: true,
    },
    productUpsellSplideSettingsCartDrawer: {
      type: Boolean,
      default: false,
    },
    products: {
      type: Object,
      default: () => ({
        count: 0,
        dynamic: [],
        section: [],
      }),
      required: true,
    },
    queryLimit: {
      type: Number,
      default: 3,
    },
    recommendations: {
      type: Object,
      default: () => ({
        enable: true,
        type: 'related',
      }),
      required: true,
    },
  },

  emits: ['has-local-products'],

  data() {
    return {

      /**
       * If recommendations are disabled then use the correct number of
       * placeholder products.
       */
      localProducts: Array(
        !this.recommendations.enable && this.products.count < this.productCount
          ? this.products.count
          : this.productCount,
      ).fill(false),
      recommendedProducts: [],
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
    }),

    /**
     * Compute cart handles.
     * @returns {Array}
     */
    cartHandles() {
      return this.isCart
        ? this.cart.items.map((item) => item.handle)
        : []
    },

    /**
     * Compute fallback products array.
     * @returns {Array}
     */
    fallbackProducts() {
      return [
        ...this.products.dynamic,
        ...this.products.section,
        ...this.products.section_collection,
      ]
    },

    /**
     * Compute if on cart page.
     * @returns {Boolean}
     */
    isCart() {
      return cnvs.page.type === 'cart'
    },

    /**
     * Compute product ID.
     * @returns {Array}
     */
    productIds() {
      if (this.overrideProductId) {
        return [this.overrideProductId]
      }

      return this.isCart
        ? this.cart.items
          .map((item) => item.product_id)
          .splice(0, this.queryLimit)
        : [this.product?.id || this.liquid?.productId]
    },
  },

  watch: {

    /**
     * Watch recommendation products to local products.
     * - Reduces down to product count if too many, no way to limit
     *   recommendation results in the storefront API query.
     * - If too few uses fallback products to meet product count.
     */
    recommendedProducts() {
      this.updateLocalProducts()
    },

    /**
     * Watch product IDs to update recommendations.
     * @param {Array} newValue - New value to compare.
     * @param {Array} oldValue - Old value.
     */
    productIds(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) {
        return
      }

      this.queryRecommendations()
    },
  },

  mounted() {
    this.queryRecommendations()
    this.hasLocalProducts()
  },

  methods: {

    /**
     * Query Shopify recommendations API.
     * - Hide component if recommendations are disabled and no fallback products
     *   have been provided.
     * - Can't query recommendations based on an array of product IDs so a query
     *   is made for each product in cart or a single product on PDP.
     * - Queries JSON API to retrieve custom URL parameters for analytics.
     */
    async queryRecommendations() {
      if (!this.recommendations.enable && !this.products.count) {
        this.hideComponent()
        return
      }

      this.containerRef.classList?.remove(this.$class('hidden'))
      this.state = 'loading'

      /**
       * If recommendations aren't enabled or product IDs array is empty then
       * load fallback products.
       */
      if (
        !this.recommendations.enable ||
        !this.productIds.filter(Boolean).length
      ) {
        this.updateLocalProducts()
        return
      }

      try {

        /**
         * Build array of recommendation arrays.
         * - Recommendation array exists for each product ID queried.
         */
        const queue = this.productIds.map((productId) => {
          return cnvs.Query({
            query: productRecommendationsQuery,
            variables: {
              country: this.$variable('store.country'),
              language: this.$variable('store.language'),
              productId: encodeLiquidId(productId, 'Product'),
              intent: this.intent,
            },
          })
        })

        const promises = await Promise.allSettled(queue)

        const recommendationArrays = promises
          .filter((promise) => {
            return promise.status === 'fulfilled' && promise.value?.productRecommendations?.length
          })
          .map((promise) => {

            /**
             * Slice the array as the Storefront API doesn't support setting a
             * product limit via a query argument.
             */
            return [...promise.value.productRecommendations.slice(0, this.productCount)]
          })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.recommendedProducts = this.flattenRecommendationArrays(recommendationArrays)

      } catch (error) {
        cnvs.ReportError('Failed to load recommendations', error)
      }
    },

    /**
     * Flatten recommendations array.
     * @param {Array} arrays - Array of recommendations array for each product.
     * @returns {Array}
     */
    flattenRecommendationArrays(arrays) {
      const recommendations = []
      let index = 0

      /**
       * Go through each recommendation array pulling a product from each until
       * the product count is reached or recommendation arrays are empty.
       */
      while (recommendations.length < this.productCount && arrays.length) {
        arrays.forEach((array, arrayIndex) => {
          if (!array[index]) {
            arrays.splice(arrayIndex, 1)
            return
          }

          const alreadyExists = recommendations.find((product) => {
            return product.handle === array[index].handle
          })

          if (alreadyExists || this.cartHandles.includes(array[index].handle)) {
            return
          }

          recommendations.push(formatProduct(array[index]))
        })

        index++
      }

      return recommendations
    },

    /**
     * Update local products.
     * - Runs when recommended products updates or recommendations are disabled.
     */
    async updateLocalProducts() {
      let localProducts = []

      if (
        this.recommendedProducts.length >= this.productCount ||
        !this.fallbackProducts.length
      ) {
        localProducts = this.recommendedProducts

      } else {
        localProducts = typeof this.fallbackProducts[0] === 'object'
          ? this.buildLocalProductsFromObjects()
          : await this.buildLocalProductsFromHandles()
      }

      this.localProducts = localProducts.slice(0, this.productCount)

      /**
       * Hide component if no local products after all products are loaded.
       * - Or if cart is empty and setting not enabled.
       */
      if (
        !this.localProducts.length ||
        (cnvs.page.type === 'cart' && !this.cart.item_count && !this.displayEmptyCart)
      ) {
        this.hideComponent()
        return
      }

      this.state = 'ready'
    },

    /**
     * Build local products when this.fallbackProducts is an array of objects.
     * - Assumes that objects are correctly formatted.
     * - Builds array of unique objects and removes products which match base
     *   product ID.
     * @returns {Array}
     */
    buildLocalProductsFromObjects() {
      const objects = [
        ...this.recommendedProducts,
        ...this.fallbackProducts,
      ]

      const uniqueObjects = Array
        .from(new Set(objects))
        .filter((product) => {
          return !this.productIds.includes(product.id)
        })

      return uniqueObjects
    },

    /**
     * Build local products when this.fallbackProducts is an array of strings.
     * - Assumes that strings are product handles.
     * - Builds array of unique handles
     * - Loads only those that need loading.
     * @returns {Promise}
     */
    buildLocalProductsFromHandles() {
      return new Promise(async(resolve, reject) => {
        try {
          const products = this.recommendedProducts

          /**
           * Go through fallback products.
           * - Ensure the product does not exist already.
           * - Load product.
           * - Ensure product does not match base product ID.
           */
          for (const fallbackHandle of this.fallbackProducts) {
            if (products.length === this.productCount) {
              break
            }

            const alreadyExists = products.find((product) => {
              return fallbackHandle === product.handle
            })

            if (alreadyExists) {
              continue
            }

            // eslint-disable-next-line no-await-in-loop
            const response = await cnvs.Query({
              query: productQuery,
              variables: {
                country: cnvs.store.country,
                handle: fallbackHandle,
                language: cnvs.store.language,
              },
            })

            const fallbackProduct = formatProduct(response)

            if (this.productIds.includes(fallbackProduct.id)) {
              continue
            }

            products.push(fallbackProduct)
          }

          resolve(products)

        } catch (error) {
          reject(error)
        }
      })
    },

    /**
     * Hide component when it's empty.
     */
    hideComponent() {
      this.containerRef.classList?.add(this.$class('hidden'))
      this.state = 'ready'
    },

    /**
     * Has local products.
     */
    hasLocalProducts() {
      if (this.location === 'cart-drawer' && this.localProducts?.length) {
        this.$emit('has-local-products', true)
      }
    },
  },
}
</script>

<style lang="scss">
@import './product-recommendations';
</style>
